<template>
<v-menu open-on-hover offset-y>
  <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
    <a class="action-icon-btn" v-bind="menuAttrs" v-on="menuOn">
      <i class="material-icons-outlined">download_for_offline</i>
    </a>
  </template>
  <v-list>
    <v-list-item v-for="(item, index) in menuItems" :key="index">
      <v-list-item-title @click="item.onClick">{{ item.title }}</v-list-item-title>
    </v-list-item>
  </v-list>
</v-menu>
</template>

<script>
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import moment from "moment";
import bulbIcon from '../../../public/static/bulb-icon.png';
import { generateSchemasWord, getReportHeaderImage } from "../../utils/download-reports";
import { whiteGlovePlans } from "@/components/plans_constants";
import { getPureServicePkgName } from "@/utils";

function getBase64FromUrl(url, callback) {
  const xhr = new XMLHttpRequest();
  xhr.onload = function () {
    const reader = new FileReader();
    reader.onloadend = function () {
      callback(reader.result);
    };
    reader.readAsDataURL(xhr.response);
  };
  xhr.open("GET", url);
  xhr.responseType = "blob";
  xhr.send();
}

export default {
  props: ['getPDFData', 'schemaType'],
    data() {
    return {
      bulbIcon: null,
      isWhiteGlove: null,
    };
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    isWhiteGloveUser() {
      if (!this.user) return false;
      const accountType = this.$store.state.selectedSubAccountRelation
        ? this.$store.state.selectedSubAccountRelation.ownerAccountType
        : this.user.accountType;

      return whiteGlovePlans.includes(getPureServicePkgName(accountType));
    },
    menuItems() {
      const baseItems = [
        { title: 'JSON', onClick: this.downloadSchema },
        { title: 'PDF', onClick: () => this.generatePDF(true) },
      ];

      if (this.isWhiteGloveUser) {
        return [
          ...baseItems,
          { title: 'PDF - Without instructions', onClick: () => this.generatePDF(false) },
          { title: 'Word', onClick: () => this.createDocx(true) },
          { title: 'Word - Without instructions', onClick: () => this.createDocx(false) },
        ];
      }

      return baseItems;
    },
  },
  methods: {
    downloadSchema() {
      this.$emit("downloadJSON");
    },

    generatePDF(includeInstructions = true) {
      getReportHeaderImage(this.$store, "schema").then((response) => {
        const doc = new jsPDF();
        doc.setDrawColor(0);
        doc.setFillColor(1, 35, 35);
        const startX = 14;
        let currentY = 10;

        doc.addImage(response?.headerImg, 'png', startX, currentY, 182, 71);

        function hexToRgb(hex) {
          const match = hex.replace('#', '').match(/.{1,2}/g);
          return match ? match.map((x) => parseInt(x, 16)) : [0, 0, 0];
        }
        const theme = response?.theme
          ? { primary: hexToRgb(response.theme.primary), secondary: hexToRgb(response.theme.secondary) }
          : { primary: hexToRgb("#093464"), secondary: hexToRgb("#F0F6FF") };

        currentY += 70;

        let tableOptions = {
          tableLineColor: theme.primary,
          tableLineWidth: 0.5,
          styles: {
            cellPadding: 5,
            valign: 'middle',
            fontSize: 12,
            textColor: [0, 0, 0],
          },
        };

        // Only add instructional table if includeInstructions is true
        if (includeInstructions) {
          const bullets = [
            'POP analyzes the schema used on top competing pages.',
            'We then filter and build the optimal custom schema for you to add to the page.',
            ''
          ];

          const instructionRows = [
            [
              {
                content: '',
                styles: {
                  minCellWidth: 40,
                  minCellHeight: 20,
                  cellPadding: 1,
                  halign: 'right',
                  valign: 'middle',
                  fillColor: theme.primary,
                  textColor: '#FFFFFF',
                },
              },
              {
                content: 'Add Schema to this page!',
                styles: {
                  minCellWidth: 40,
                  minCellHeight: 20,
                  cellPadding: 1,
                  halign: 'left',
                  valign: 'middle',
                  fillColor: theme.primary,
                  fontSize: 22.65,
                  fontStyle: 'bold',
                  textColor: '#FFFFFF',
                },
              },
            ],
            [
              {
                colSpan: 2,
                content: bullets.join('\n'),
                styles: {
                  halign: 'center',
                  fillColor: theme.secondary,
                  fontSize: 12,
                  lineHeight: 20,
                  fontStyle: 'normal',
                  textColor: '#000000',
                },
              },
            ],
          ];

          tableOptions.startY = currentY + 8;
          tableOptions.body = instructionRows;

          tableOptions.didDrawCell = ((imageSrc) => function (data) {
            const doc = data.doc;

            // Add bulb icon in first row first cell
            if (data.row.index === 0 && data.column.index === 0) {
              const { x, y, width, height } = data.cell;
              const imgWidth = height - 4;
              const imgX = x + width - imgWidth - 2;
              doc.addImage(imageSrc, 'png', imgX + 8, y + 4, 10, 10);
            }

            // Add help link in instructions row
            if (data.row.index === 1 && data.column.index === 0) {
              const textBeforeLink = 'For instructions on how to publish schema to your page, ';
              const linkText = 'click here.';
              const linkY = data.cell.y + data.cell.height - 6;
              const cellWidth = data.cell.width;
              const totalTextWidth =
                doc.getTextWidth(textBeforeLink) + doc.getTextWidth(linkText);
              const centeredX = data.cell.x + (cellWidth - totalTextWidth) / 2;

              doc.text(textBeforeLink, centeredX, linkY);
              const linkStartX = centeredX + doc.getTextWidth(textBeforeLink);

              doc.setTextColor(255, 87, 0);
              doc.text(linkText, linkStartX, linkY);

              const underlineY = linkY + 1;
              const linkWidth = doc.getTextWidth(linkText);
              doc.setDrawColor(255, 87, 0);
              doc.setLineWidth(0.5);
              doc.line(linkStartX, underlineY, linkStartX + linkWidth, underlineY);

              doc.link(linkStartX, linkY - 10, linkWidth, 10, {
                url: 'https://help.pageoptimizer.pro/article/how-to-add-pageoptimizer-pros-schema-to-your-pages'
              });
            }
          })(this.bulbIcon);

          autoTable(doc, tableOptions);
          currentY = doc.lastAutoTable.finalY + 10;
        } else {
          currentY += 10; // Add spacing if no instructions
        }

        // Add schema table (always included)
        const jsonContent = this.getPDFData();
        const scriptWrappedJson = `<script type="application/ld+json">\n${jsonContent}\n<\/script>`;

        const schemaRows = [
          [
            {
              content: `${this.schemaType || ''} Schema`,
              styles: {
                fillColor: theme.secondary,
                fontSize: 22.65,
                fontStyle: 'bold',
                textColor: '#000000',
              },
            },
          ],
          [
            {
              content: scriptWrappedJson,
              styles: {
                fontStyle: 'monospace',
                fillColor: theme.secondary,
                fontSize: 12,
                textColor: '#000000',
              },
            },
          ],
        ];

        autoTable(doc, {
          startY: currentY,
          tableLineColor: theme.primary,
          tableLineWidth: 0.5,
          styles: {
            cellPadding: 5,
            valign: 'middle',
            fontSize: 12,
            textColor: [0, 0, 0],
          },
          body: schemaRows,
        });

        // Save PDF
        const timestamp = moment().format('YYYY_M_D_h_mm_ss');
        doc.save(`google_json_${timestamp}.pdf`);
      });
    },

    createDocx(includeInstructions = true) {
      getReportHeaderImage(this.$store, "schema").then((responseData) => {
        const schemas = [
          {
            type: "Organization",
            schema: this.getPDFData()
          }
        ];
        generateSchemasWord({
          theme: responseData.theme,
          headerImg: responseData.headerImg,
          data: {
            schemas,
            pageType: "schema",
            mainKeyword: "<main-keyword>"
          },
          includeInstructions
        });
      });
    },
  },

  mounted() {
    getBase64FromUrl(bulbIcon, (base64) => {
      this.bulbIcon = base64;
    });
  },
};
</script>

<style scoped lang="scss">
.action-icon-btn {
  background: var(--v-lightYellowColor-base);
  display: inline-flex;
  align-items: center;
  padding: 5px;

  & > i {
    color: var(--v-profileAnchorColor-base);
  }
}
</style>
